export class ApiError extends Error {
  status: number;
  data?: unknown;
  userMessage?: string;
  technicalMessage?: string;

  constructor(
    message: string,
    status: number,
    data?: unknown,
    userMessage?: string,
    technicalMessage?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.userMessage = userMessage;
    this.technicalMessage = technicalMessage;
  }
}

export type ApiClientConfig = {
  baseUrl: string;
  getToken?: () => string | null;
};

type RequestOptions = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
};

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

const parseJsonSafely = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
};

export const createApiClient = ({ baseUrl, getToken }: ApiClientConfig) => {
  const request = async <T>(path: string, options: RequestOptions = {}) => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = getToken ? getToken() : null;
    if (token) {
      headers['x-token-auth'] = token;
    }

    let lastError: unknown;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(`${baseUrl}${path}`, {
          ...options,
          headers,
        });

        const data = await parseJsonSafely(response);

        if (!response.ok) {
          let message = response.statusText || 'Request failed';
          if (data && typeof data === 'object' && 'error' in data) {
            const error = data.error as { detail?: string };
            message = error.detail || message;
          }
          throw new ApiError(String(message), response.status, data ?? undefined);
        }

        return data as T;
      } catch (err) {
        if (err instanceof ApiError) {
          throw err;
        }
        lastError = err;
        if (attempt < MAX_RETRIES) {
          await sleep(RETRY_DELAY_MS);
        }
      }
    }

    const method = options.method ?? 'GET';
    const originalMessage = lastError instanceof Error ? lastError.message : String(lastError);
    throw new ApiError(
      originalMessage,
      0,
      undefined,
      'El servidor está ocupado, vuelva a intentar en un momento.',
      `${method} ${path} falló después de ${MAX_RETRIES} intentos: ${originalMessage}`
    );
  };

  return {
    get: <T>(path: string) => request<T>(path),
    post: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
    put: <T>(path: string, body?: unknown) =>
      request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
    del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  };
};
